import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import type { RadioChangeEvent, FormInstance } from "antd";
import {
  Radio,
  Form,
  Input,
  Select,
  Button,
  InputNumber,
  DatePicker,
  message,
  Space,
  Spin,
  Switch,
} from "antd";
import locale from "antd/es/date-picker/locale/zh_CN";
import Breadcrumb from "./components/Breadcrumb";
// import TankInfo from "./components/TankInfo";
import "dayjs/locale/zh-cn";
import "./index.less";
import { quictApi } from "@/apis";
import { useUserInfo } from "@/model";
import type { RangePickerProps } from "antd/es/date-picker";
import dayjs from "dayjs";
import TtsItemInfo from "../liquidLevel/ttsItemInfo";
import DayObj from "./CONST";
import moment from "moment";

const { TextArea } = Input;

// 自提时间不能为当前时间之前
const disabledDateA: RangePickerProps["disabledDate"] = (current) => {
  return current && current < dayjs().startOf("day");
};

const QuickOrder = () => {
  const navigate = useNavigate();
  const formRef = useRef<FormInstance>(null);
  const { id = "" } = useParams();
  const userInfo = useUserInfo();

  const [loading, setLoading] = useState(false);

  const [productList, setProductList] = useState<ProductListItem[]>([]); // 产品列表
  const [curProduct, setCurProduct] = useState<ProductListItem>(); // 已选产品
  const [curDeleveyMethod, setCurDeleveyMethod] = useState<string>(); // 当前配送方式
  const [isOrderTag, setIsOrderTag] = useState<boolean>(true); // 是否订单收货联系人

  // 下拉框选项
  const [ttsOptions, setTtsOptions] = useState<TTsListItem[]>([]); // 储罐序列号下拉框
  const [contactInfoOptions, setContactInfoOptions] = useState([]);

  const [tTsInfo, setTTsInfo] = useState<TTsInfo | undefined>(); // tts信息
  const [disableTimeInfo, setDisableTimeInfo] = useState(DayObj["999"]);

  // 改变配送方式
  const changeDeleveyMethod = useCallback((val: string) => {
    onReset();
    formRef.current?.setFieldsValue({
      deleveyMethod: val,
    });
    setCurDeleveyMethod(val);
  }, []);

  // 根据储罐编码获取合同信息和液位信息
  const getInfobyTankNo = useCallback((tankNo = "") => {
    quictApi
      .getContract4wxOrderCreate({ tankNo })
      .then((res) => {
        setDisableTimeInfo(DayObj[res?.orderMode || "999"]);
      })
      .catch((err) => {
        setDisableTimeInfo(DayObj["999"]);
        message.error(err);
      });
    quictApi
      .findByTtsId({
        tankNum: tankNo,
      })
      .then((res) => {
        if (res && res.length) {
          setTTsInfo(res[0]);
        } else {
          setTTsInfo(undefined);
        }
      })
      .catch((err) => {
        message.error(err);
        setTTsInfo(undefined);
      });
  }, []);

  // 获取联系人信息
  const getContactInfo = useCallback((cusCode = "", kunsh = "") => {
    quictApi
      .findAllByConditions({
        cusCode,
        kunsh,
      })
      .then((res) => {
        const { success, result = [] } = res;
        if (success) {
          const _result = result.map((item: any) => ({
            label: `${item.contactName}-${item.contactPhone}`,
            valueName: item.contactName,
            valuePhone: item.contactPhone,
            isFlag: item.isFlag === "Y",
            dataSource: item.dataSource,
          }));
          setContactInfoOptions(_result);
        }
      })
      .catch((err) => {
        message.error(err);
      });
  }, []);

  // 获取储罐编码
  const getTtsOptions = useCallback(
    async (customerCode = "", servedCode = "", productCode = "") => {
      setTTsInfo(undefined);
      formRef.current?.setFieldsValue({
        tankNo: "",
      });
      try {
        const res = await quictApi.findByContactCode({
          customerCode,
          servedCode,
          productCode,
        });
        setTtsOptions(res);
        if (res && res.length === 1) {
          formRef.current?.setFieldsValue({
            tankNo: res[0].tankNo,
          });
          getInfobyTankNo(res[0].tankNo);
        }
      } catch (error: any) {
        message.error(error);
        setTtsOptions([]);
      }
    },
    [getInfobyTankNo]
  );

  // 控制默认选中储罐地址
  useEffect(() => {
    if (
      userInfo.curUserInfo &&
      curDeleveyMethod === "A" &&
      curProduct &&
      curProduct.kunshMapList &&
      curProduct.kunshMapList.length === 1
    ) {
      const _kunsh = curProduct.kunshMapList[0].kunsh;
      formRef.current?.setFieldsValue({
        kunsh: _kunsh,
      });
      getTtsOptions(
        userInfo.curUserInfo?.customerCode,
        _kunsh,
        curProduct?.productId
      );
      getContactInfo(userInfo.curUserInfo?.customerCode, _kunsh);
    }
  }, [
    curProduct,
    curDeleveyMethod,
    userInfo.curUserInfo,
    getTtsOptions,
    getContactInfo,
  ]);

  useEffect(() => {
    // 获取产品列表信息
    if (userInfo.curUserInfo) {
      quictApi
        .getListByCustomerCode2({
          customerCode: userInfo.curUserInfo.userCode,
          productGroup: userInfo.curUserInfo.productGroup,
        })
        .then((res) => {
          // console.log(res);
          setProductList(res);
          // 默认选中第一个产品
          if (res && res.length > 0) {
            setCurProduct(res[0]);
            changeDeleveyMethod(res[0].deleveyMethod);
          }
        })
        .catch((err) => {
          message.error(err);
        });
    }
  }, [userInfo.curUserInfo, changeDeleveyMethod]);

  const handleFormChange = (changedValues: any) => {
    const keys = Object.keys(changedValues);
    // console.log("handleFormChange", changedValues, keys);
    // 配送方式切换
    if (keys.includes("deleveyMethod")) {
      changeDeleveyMethod(changedValues.deleveyMethod);
    }
    // 切换储罐地址
    if (keys.includes("kunsh")) {
      getTtsOptions(
        userInfo.curUserInfo?.customerCode,
        changedValues.kunsh,
        curProduct?.productId
      );
      getContactInfo(userInfo.curUserInfo?.customerCode, changedValues.kunsh);
    }
    // 切换储罐序列号
    if (keys.includes("tankNo")) {
      getInfobyTankNo(changedValues.tankNo);
    }
  };

  const handleChangeRadio = (e: RadioChangeEvent) => {
    const _curProduct = productList.filter(
      (el) => el.productId === e.target.value
    )[0];
    changeDeleveyMethod(_curProduct.deleveyMethod);
    setCurProduct(_curProduct);
    setContactInfoOptions([]);
  };

  const disabledDateC = useMemo(() => {
    return (current: any) => {
      const _curTime = dayjs(current).valueOf();
      if (disableTimeInfo) {
        const { minDays, maxDays, hour, minute } = disableTimeInfo;
        const midTime = new Date().setHours(hour, minute, 0, 0);
        const now = +new Date();
        const startDays = now > midTime ? maxDays : minDays;
        const endDays = 12 + startDays;
        // T+2表示，T就代表今天，可以选今天+2那天
        const startTime = moment()
          .add(startDays - 1, "days")
          .valueOf();
        const endTime = moment().add(endDays, "days").valueOf();
        return _curTime < startTime || _curTime > endTime;
      } else {
        const preTime = moment().subtract(1, "days").valueOf();
        return _curTime < preTime;
      }
    };
  }, [disableTimeInfo]);

  const handleChangeContact = useCallback(
    (value: any[], curKey: string) => {
      if (value.length) {
        const curVal = value.reverse()[0];
        const curOption: any = contactInfoOptions.find(
          (item) =>
            item[curKey === "contactName" ? "valueName" : "valuePhone"] ===
            curVal
        );

        let isOrderTag = true;
        let contactName = formRef.current?.getFieldValue("contactName");
        let contactPhone = formRef.current?.getFieldValue("contactPhone");
        // 如果curOption存在，则直接使用curOption
        if (curOption) {
          contactName = [curOption.valueName];
          contactPhone = [curOption.valuePhone];
          isOrderTag = curOption.dataSource === "20";
        } else {
          // 如果curOption不存在，则使用curVal
          if (curKey === "contactName") {
            contactName = [curVal];
          } else {
            contactPhone = [curVal];
          }
        }

        formRef.current?.setFieldsValue({
          contactName,
          contactPhone,
        });
        setIsOrderTag(isOrderTag);
      } else {
        formRef.current?.setFieldsValue({
          contactName: [],
          contactPhone: [],
        });
      }
    },
    [contactInfoOptions]
  );

  const onFinish = async (values: any) => {
    try {
      const {
        deleveyMethod,
        quantity,
        carNumber,
        deliverydate,
        texth,
        kunsh,
        tankNo,
        ztime,
        contactName,
        contactPhone,
        isFlag,
      } = values;

      // 配送需要校验有没有超过最大容量
      let ttsMakeNum = "";
      ttsOptions.forEach(({ tankNo: _tankNo, ttsMakeNum: _ttsMakeNum }) => {
        if (tankNo === _tankNo) {
          ttsMakeNum = _ttsMakeNum;
        }
      });
      if (
        deleveyMethod === "A" &&
        tTsInfo &&
        Number(quantity) > Number(tTsInfo?.filledWeight) &&
        ttsMakeNum !== tankNo
      ) {
        message.error("下单数量不能超过储罐最大容量");
        return;
      }
      const {
        contractNo,
        kunshMapList = [],
        productId,
        productName,
        productSeqNo,
      } = curProduct || {};
      const addressList = kunshMapList.filter(
        ({ kunsh: _kunsh }) => _kunsh === kunsh
      );
      const address = addressList.length > 0 ? addressList[0].address : "";
      const {
        userName: ordercreate,
        userAccount: ordercreatecode,
        customerName: cusName,
        customerCode: cusCode,
        userCode: customerCode,
      } = userInfo.curUserInfo || {};

      const params = {
        sdabw: deleveyMethod,
        tankNo,
        ttsMakeNum,
        kunsh,
        zsorc: "A", // 标识pc
        contractCode: contractNo, // 转换合同字段
        deliverydate: dayjs(deliverydate).format("YYYY-MM-DD"),
        address,
        texth,
        ztime,
        carNumber,
        ordercreate,
        ordercreatecode,
        cusName,
        cusCode,
        customerCode,
        orderProductList: [
          {
            quantity,
            productid: productId,
            productname: productName,
            seqNo: productSeqNo,
            id: "",
          },
        ],
        contact: contactName[0], // 联系人
        tel: contactPhone[0], // 联系人手机
      };

      setLoading(true);
      // 先校验订单是否重复
      const checkRes = await quictApi.quickCreateListCheck([params]);
      const { result: _checkRes, message: _checkMes } = checkRes;
      // 订单重复，中断流程
      if (_checkRes && _checkMes) {
        message.error(_checkMes);
        setLoading(false);
        return;
      }
      const { message: _creMes, success } = await quictApi.quickCreateList([
        params,
      ]);
      // 数据来源：20订单收货联系人 情况下才调用接口，防止数据冗余
      if (isOrderTag) {
        quictApi.createOrUpdate({
          contactName: contactName[0],
          contactPhone: contactPhone[0],
          cusCode,
          cusName,
          kunsh,
          isFlag: isFlag ? "Y" : "N",
          userName: ordercreate,
          userAccount: ordercreatecode,
        });
      }
      if (success && _creMes) {
        setLoading(false);
        const dataInfo = _creMes.split("_");
        if (dataInfo[0] === "01") {
          message.success("订单提交成功,即将为您跳转到订单详情");
          setTimeout(() => {
            navigate(`/service/orderDetail?id=${dataInfo[1]}`);
          }, 3000);
        } else {
          message.error({
            content:
              "尊敬的客户，由于您有未结清欠款，当前订单已被冻结，暂无法配送，请及时结清欠款。待结清后，本订单将自动解除，并尽快为您安排配送。",
            duration: 10,
          });
        }
      } else {
        message.error("订单提交失败");
        setLoading(false);
      }
    } catch (error: any) {
      message.error(error);
      setLoading(false);
      // console.log(error);
    }
  };

  // const onFinishFailed = (errorInfo: any) => {
  //   console.log("Failed:", errorInfo);
  //   navigate(`/service/orderDetail?id=${'26991c44a4094728b48f46d3eeaf0c7c'}`);
  // };

  const onReset = () => {
    formRef.current?.resetFields();
    setTtsOptions([]);
    setTTsInfo(undefined);
    setDisableTimeInfo(DayObj["999"]);
  };

  return (
    <>
      <Breadcrumb />
      <article className="max-width quick-order-wrapper">
        <h3 className="title">合同产品下单</h3>
        <Spin spinning={loading}>
          <Radio.Group
            className="product-radio"
            onChange={handleChangeRadio}
            value={curProduct?.productId}
          >
            {productList.map((el) => (
              <Radio value={el.productId}>{el.productName}</Radio>
            ))}
          </Radio.Group>
          <section className="form-wrapper">
            <Form
              className="product-from form-item"
              name="basic"
              ref={formRef}
              labelCol={{ span: 6 }}
              // wrapperCol={{ span: 16 }}
              // style={{ maxWidth: 600 }}
              // initialValues={{ remember: true }}
              onFinish={onFinish}
              // onFinishFailed={onFinishFailed}
              autoComplete="off"
              onValuesChange={handleFormChange}
              initialValues={{
                isFlag: true,
              }}
            >
              <Form.Item
                className="product-form-item"
                label="配送方式"
                name="deleveyMethod"
              >
                {curProduct && curProduct.deleveyMethodMapList.length > 1 ? (
                  <Select
                    fieldNames={{ value: "code" }}
                    options={curProduct.deleveyMethodMapList}
                  />
                ) : (
                  <span>{curProduct?.deleveyMethodMapList[0].label}</span>
                )}
              </Form.Item>
              {curDeleveyMethod === "C" ? (
                <>
                  {/* 自提 */}
                  <Form.Item
                    label="下单数量"
                    name="Quantity"
                    className="product-form-item"
                    rules={[{ required: true, message: "" }]}
                  >
                    <Space>
                      <Form.Item
                        name="quantity"
                        rules={[
                          { required: true, message: "下单数量不能为空!" },
                        ]}
                        noStyle
                      >
                        <InputNumber
                          min={0.01}
                          max={9999}
                          style={{ marginRight: "10px" }}
                        />
                      </Form.Item>
                      吨
                    </Space>
                  </Form.Item>
                  <Form.Item
                    label="提货车牌号"
                    name="carNumber"
                    className="product-form-item"
                    rules={[{ required: true, message: "提货车牌号不能为空!" }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    label="提货时间"
                    name="deliverydate"
                    className="product-form-item"
                    rules={[{ required: true, message: "提货时间不能为空!" }]}
                  >
                    <DatePicker disabledDate={disabledDateA} locale={locale} />
                  </Form.Item>
                </>
              ) : (
                <>
                  {/* 配送 */}
                  <Form.Item
                    label="储罐地址"
                    name="kunsh"
                    className="product-form-item"
                    rules={[{ required: true, message: "储罐地址不能为空!" }]}
                  >
                    <Select
                      fieldNames={{ value: "kunsh", label: "address" }}
                      options={curProduct?.kunshMapList}
                    />
                  </Form.Item>
                  <Form.Item
                    label="储罐序列号"
                    name="tankNo"
                    className="product-form-item"
                    rules={[{ required: true, message: "储罐序列号不能为空!" }]}
                  >
                    <Select
                      fieldNames={{ value: "tankNo", label: "ttsMakeNum" }}
                      options={ttsOptions}
                    />
                  </Form.Item>
                  <Form.Item
                    label="下单数量"
                    name="Quantity"
                    className="product-form-item"
                    rules={[{ required: true, message: "" }]}
                  >
                    <Space>
                      <Form.Item
                        name="quantity"
                        className="product-form-item"
                        rules={[
                          { required: true, message: "下单数量不能为空!" },
                        ]}
                        noStyle
                      >
                        <InputNumber
                          min={0.01}
                          max={9999}
                          style={{ marginRight: "10px" }}
                        />
                      </Form.Item>
                      吨
                    </Space>
                  </Form.Item>
                  <Form.Item
                    tooltip="如您有紧急配送需求，请拨打客服热线400-921-1717为您服务"
                    label="希望送达日期"
                    name="deliverydate"
                    className="product-form-item"
                    rules={[
                      { required: true, message: "希望送达日期不能为空!" },
                    ]}
                  >
                    <DatePicker disabledDate={disabledDateC} locale={locale} />
                  </Form.Item>
                  <Form.Item
                    label="期望送达时间"
                    name="ztime"
                    className="product-form-item"
                    rules={[
                      { required: true, message: "期望送达时间不能为空!" },
                    ]}
                  >
                    <Select
                      options={[
                        { value: "23:00", label: "全天" },
                        { value: "18:00", label: "8:00-18:00" },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item
                    className="product-form-item"
                    label="联系人姓名"
                    name="contactName"
                    rules={[{ required: true, message: "联系人姓名不能为空!" }]}
                  >
                    <Select
                      mode="tags"
                      style={{ width: "100%" }}
                      placeholder="请输入或者选择联系人姓名"
                      options={contactInfoOptions}
                      fieldNames={{ label: "label", value: "valueName" }}
                      onChange={(val) => {
                        handleChangeContact(val, "contactName");
                      }}
                      optionLabelProp="value"
                    />
                  </Form.Item>
                  <Form.Item
                    className="product-form-item"
                    label="联系人手机号码"
                    name="contactPhone"
                    rules={[
                      { required: true, message: "联系人手机号码不能为空!" },
                    ]}
                  >
                    <Select
                      mode="tags"
                      style={{ width: "100%" }}
                      placeholder="请输入或者选择联系人手机号码"
                      options={contactInfoOptions}
                      fieldNames={{ label: "label", value: "valuePhone" }}
                      onChange={(val) => {
                        handleChangeContact(val, "contactPhone");
                      }}
                      optionLabelProp="value"
                    />
                  </Form.Item>
                  <Form.Item
                    className="product-form-item"
                    label="联系人设置为默认"
                    name="isFlag"
                  >
                    <Switch defaultChecked />
                  </Form.Item>
                </>
              )}
              <Form.Item
                className="product-form-item"
                label="备注"
                name="texth"
              >
                <TextArea placeholder="请说明您的配送需求，如需要自增压车配送等" />
              </Form.Item>

              <Form.Item
                className="product-form-submit"
                wrapperCol={{ offset: 6, span: 20 }}
              >
                <Button type="primary" size="large" htmlType="submit">
                  提交订单
                </Button>
              </Form.Item>
            </Form>
            {tTsInfo ? (
              <TtsItemInfo ttsInfo={tTsInfo} className="tts-content">
                <div className="tank-tips">
                  <p>* TTS属于监控工具，仅用于查看储罐液位及下订单参考</p>
                  <p>* 实际卸货量以合同约定的流量计/地磅为准</p>
                </div>
              </TtsItemInfo>
            ) : (
              <></>
            )}
          </section>
        </Spin>
      </article>
    </>
  );
};

export default QuickOrder;
